import api from './api';
import { ApiResponse, PaginatedResponse } from '../types';

export interface CreativeSettings {
  meta_page_id?: string;
  meta_instagram_id?: string;
  linkedin_org_urn?: string;
  [key: string]: any;
}

export interface Creative {
  id: string;
  user_id: string;
  name: string;
  headlines: string[];
  descriptions: string[];
  image_url: string | null;
  video_url: string | null;
  cta_text: string | null;
  destination_url: string | null;
  tags: string[];
  platform: string[];
  is_favorite: boolean;
  usage_count: number;
  settings?: CreativeSettings | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCreativeData {
  name: string;
  headlines?: string[];
  descriptions?: string[];
  image_url?: string | null;
  video_url?: string | null;
  cta_text?: string | null;
  destination_url?: string | null;
  tags?: string[];
  platform?: string[];
  settings?: CreativeSettings | null;
}

export interface UpdateCreativeData extends Partial<CreateCreativeData> {
  is_favorite?: boolean;
}

export interface CreativeFilters {
  page?: number;
  limit?: number;
  search?: string;
  platform?: string;
  tags?: string;
  is_favorite?: boolean;
  sort_by?: 'created_at' | 'updated_at' | 'name' | 'usage_count';
  sort_order?: 'ASC' | 'DESC';
}

export const creativeService = {
  /**
   * Get all creatives with filters and pagination
   */
  async getCreatives(filters: CreativeFilters = {}): Promise<PaginatedResponse<Creative>> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.search) params.append('search', filters.search);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.tags) params.append('tags', filters.tags);
    if (filters.is_favorite) params.append('is_favorite', 'true');
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/creatives?${params.toString()}`);
    return response.data;
  },

  /**
   * Get single creative by ID
   */
  async getCreative(id: string): Promise<ApiResponse<Creative>> {
    const response = await api.get(`/creatives/${id}`);
    return response.data;
  },

  /**
   * Create new creative
   */
  async createCreative(data: CreateCreativeData): Promise<ApiResponse<Creative>> {
    const response = await api.post('/creatives', data);
    return response.data;
  },

  /**
   * Update existing creative
   */
  async updateCreative(id: string, data: UpdateCreativeData): Promise<ApiResponse<Creative>> {
    const response = await api.put(`/creatives/${id}`, data);
    return response.data;
  },

  /**
   * Delete creative
   */
  async deleteCreative(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/creatives/${id}`);
    return response.data;
  },

  /**
   * Duplicate creative
   */
  async duplicateCreative(id: string): Promise<ApiResponse<Creative>> {
    const response = await api.post(`/creatives/${id}/duplicate`);
    return response.data;
  },

  /**
   * Increment usage count (when creative is used in campaign)
   */
  async incrementUsage(id: string): Promise<ApiResponse<Creative>> {
    const response = await api.post(`/creatives/${id}/increment-usage`);
    return response.data;
  },

  /**
   * Toggle favorite status
   */
  async toggleFavorite(id: string, is_favorite: boolean): Promise<ApiResponse<Creative>> {
    const response = await api.put(`/creatives/${id}`, { is_favorite });
    return response.data;
  }
};

export default creativeService;
